import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../../components/ui/tabs';
import { Sparkles, Mail, Plus, CheckCircle2, XCircle, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

const StatusPill = ({ s }) => {
  const map = {
    active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    extended: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
    expired: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    converted: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
    lost: 'bg-slate-500/10 text-slate-400 border-slate-500/30',
    grandfathered: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    admin: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
  };
  return <Badge variant="outline" className={`text-[10px] ${map[s] || ''}`}>{s}</Badge>;
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString() : '—';

export default function TrialsAdmin() {
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [actionId, setActionId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([
        axios.get(`${API}/trial/admin/users`),
        axios.get(`${API}/trial/admin/requests`),
      ]);
      setUsers(u.data.users || []);
      setRequests(r.data.requests || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load trials');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const s = { active: 0, expired: 0, extended: 0, converted: 0, lost: 0, total: 0 };
    users.forEach((u) => {
      const st = u.state?.status;
      if (st in s) s[st] += 1;
      if (u.state?.has_trial) s.total += 1;
    });
    return s;
  }, [users]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.name || '').toLowerCase().includes(q) ||
      (u.state?.status || '').toLowerCase().includes(q)
    );
  }, [filter, users]);

  const extend = async (uid, days) => {
    setActionId(uid);
    try {
      await axios.post(`${API}/trial/admin/users/${uid}/extend`, { days });
      toast.success(`Granted +${days} days`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Extend failed');
    } finally { setActionId(null); }
  };
  const setStatus = async (uid, status) => {
    setActionId(uid);
    try {
      await axios.post(`${API}/trial/admin/users/${uid}/status`, { status });
      toast.success(`Marked as ${status}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status update failed');
    } finally { setActionId(null); }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="trials-admin-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-cyan-400" /> Trial Funnel
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage 7-day Pro trials, grant extensions, and track conversion.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Active', value: stats.active, color: 'text-emerald-400' },
          { label: 'Extended', value: stats.extended, color: 'text-cyan-400' },
          { label: 'Expired', value: stats.expired, color: 'text-rose-400' },
          { label: 'Converted', value: stats.converted, color: 'text-violet-400' },
          { label: 'Lost', value: stats.lost, color: 'text-slate-400' },
        ].map((s) => (
          <Card key={s.label} className="bg-card border-border">
            <CardContent className="pt-4">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" data-testid="trials-tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="requests" data-testid="trials-tab-requests">Extension Requests ({requests.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-3">
          <div className="relative max-w-sm">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
            <Input value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by email / name / status…"
              className="pl-8 bg-secondary" data-testid="trials-filter" />
          </div>
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  No trial users yet — they'll appear here automatically after first Google sign-in.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Days left</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Ends</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((u) => {
                      const st = u.state || {};
                      return (
                        <TableRow key={u.user_id} data-testid={`trial-row-${u.user_id}`}>
                          <TableCell className="font-mono text-xs">{u.email}</TableCell>
                          <TableCell><StatusPill s={st.status || '—'} /></TableCell>
                          <TableCell className="text-xs">{st.is_active ? `${st.days_left ?? '∞'} d` : '—'}</TableCell>
                          <TableCell className="text-xs">{fmtDate(u.trial_started_at)}</TableCell>
                          <TableCell className="text-xs">{fmtDate(u.trial_extended_until || u.trial_ends_at)}</TableCell>
                          <TableCell className="text-right space-x-1">
                            {st.has_trial && (
                              <>
                                <Button size="sm" variant="outline" className="h-7 text-xs"
                                  onClick={() => extend(u.user_id, 7)}
                                  disabled={actionId === u.user_id}
                                  data-testid={`trial-extend-7-${u.user_id}`}>
                                  +7
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 text-xs"
                                  onClick={() => extend(u.user_id, 14)}
                                  disabled={actionId === u.user_id}
                                  data-testid={`trial-extend-14-${u.user_id}`}>
                                  +14
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-violet-400"
                                  onClick={() => setStatus(u.user_id, 'converted')}
                                  disabled={actionId === u.user_id}
                                  title="Mark as converted to paid">
                                  <CheckCircle2 className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-400"
                                  onClick={() => setStatus(u.user_id, 'lost')}
                                  disabled={actionId === u.user_id}
                                  title="Mark as lost">
                                  <XCircle className="w-3 h-3" />
                                </Button>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requests">
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              {requests.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  No extension requests yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Use case</TableHead>
                      <TableHead>Requested</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((r) => (
                      <TableRow key={`${r.user_id}-${r.created_at}`}>
                        <TableCell className="font-medium">{r.name}</TableCell>
                        <TableCell className="text-xs">{r.company || '—'}</TableCell>
                        <TableCell className="text-xs max-w-xs truncate" title={r.use_case}>{r.use_case || '—'}</TableCell>
                        <TableCell className="text-xs">{r.requested_days}d</TableCell>
                        <TableCell><StatusPill s={r.status} /></TableCell>
                        <TableCell className="text-right">
                          {r.status === 'pending' && (
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              onClick={() => extend(r.user_id, r.requested_days || 7)}
                              data-testid={`grant-${r.user_id}`}>
                              <Plus className="w-3 h-3 mr-1" />Grant
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
